import { useState } from "react";
import { BookOpen, RefreshCw } from "lucide-react";
import { toast } from "@/lib/toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useDisconnectMendeley,
  useMendeleyConnect,
  useMendeleyFolders,
  useMendeleySource,
  useSetMendeleyFolders,
} from "@/hooks/useMendeleySource";

/**
 * Mendeley sync card — internal preview (ring 0). Rendered only for
 * users with the `internal` tag; promote by widening the check.
 */
export function MendeleyCard() {
  const { data: me } = useCurrentUser();
  const internal = me?.tags?.includes("internal") ?? false;
  const { data: source } = useMendeleySource(internal);
  const connect = useMendeleyConnect();
  const disconnect = useDisconnectMendeley();
  const setFolders = useSetMendeleyFolders();
  const [pickingFolders, setPickingFolders] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const folders = useMendeleyFolders(pickingFolders && internal);

  if (!internal) return null;

  const onConnect = () => {
    connect.mutate(undefined, {
      onError: () => toast.error("Couldn't start the Mendeley connection"),
    });
  };

  const openFolderPicker = () => {
    setPicked(new Set(source?.folder_ids ?? []));
    setPickingFolders(true);
  };

  const toggleFolder = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveFolders = async () => {
    try {
      await setFolders.mutateAsync(Array.from(picked));
      setPickingFolders(false);
      toast.success(
        picked.size > 0
          ? `Watching ${picked.size} folder${picked.size === 1 ? "" : "s"}`
          : "Watching your whole Mendeley library",
      );
    } catch {
      toast.error("Couldn't save folder selection");
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <BookOpen className="size-4" />
          Mendeley
          <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
            Internal preview
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!source && (
          <>
            <p className="text-xs text-muted-foreground">
              Auto-attach PDFs from your Mendeley library. Connects via
              your Elsevier account — PaperChase never sees your password.
            </p>
            <Button size="sm" onClick={onConnect} disabled={connect.isPending}>
              {connect.isPending ? "Redirecting…" : "Connect Mendeley"}
            </Button>
          </>
        )}

        {source && source.status === "needs_reauth" && (
          <div className="space-y-2">
            <p className="text-xs text-amber-600">
              Mendeley connection expired — reconnect to resume syncing.
              {source.status_reason ? ` (${source.status_reason})` : ""}
            </p>
            <Button size="sm" onClick={onConnect} disabled={connect.isPending}>
              <RefreshCw className="mr-1 size-3" /> Reconnect
            </Button>
          </div>
        )}

        {source && source.status === "active" && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Connected{source.display_name ? ` as ${source.display_name}` : ""}.
              {" "}
              {source.folder_ids.length > 0
                ? `Watching ${source.folder_ids.length} folder${source.folder_ids.length === 1 ? "" : "s"}.`
                : "Watching your whole library."}
              {source.last_polled_at
                ? ` Last sync ${new Date(source.last_polled_at).toLocaleString()}.`
                : " First sync runs within ~5 minutes."}
            </p>

            {!pickingFolders && (
              <Button variant="outline" size="sm" onClick={openFolderPicker}>
                Choose folders
              </Button>
            )}
            {pickingFolders && (
              <div className="space-y-2 rounded border p-2">
                {folders.isLoading && (
                  <p className="text-xs text-muted-foreground">Loading folders…</p>
                )}
                {folders.data?.folders.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No folders in this library — whole-library sync applies.
                  </p>
                )}
                {folders.data?.folders.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={picked.has(f.id)}
                      onChange={() => toggleFolder(f.id)}
                    />
                    {f.name}
                  </label>
                ))}
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={saveFolders} disabled={setFolders.isPending}>
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPickingFolders(false)}
                  >
                    Cancel
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  No folders selected = watch the whole library.
                </p>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={() =>
                disconnect.mutate(undefined, {
                  onSuccess: () => toast.success("Mendeley disconnected"),
                })
              }
            >
              Disconnect
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
